/**
 * Quantum Readiness — platform guide at /my/learn/quantum-readiness.
 *
 * Explains what the Quantum Readiness agent evaluates, the maturity
 * tiers, and the standards the recommendations map to. Linked from
 * the Learn index. Sibling of /my/learn/scoring.
 */
import { Link } from 'react-router-dom';
import { ArrowLeft, Atom, ShieldCheck, AlertTriangle, ExternalLink } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

const TIERS: Array<{ tier: number; name: string; tone: string; bullets: string[] }> = [
  {
    tier: 0, name: 'Quantum-vulnerable', tone: 'bg-red-500/10 border-red-500/30 text-red-700 dark:text-red-400',
    bullets: [
      'Classical RSA, ECDSA, ECDH only across TLS, JWT, SSH.',
      'Possibly AES-128 — Grover halves to ~64-bit, no longer adequate.',
      'Possibly SHA-1 anywhere outside content addressing.',
      'Most internet-facing services land here today.',
    ],
  },
  {
    tier: 1, name: 'Quantum-aware', tone: 'bg-orange-500/10 border-orange-500/30 text-orange-700 dark:text-orange-400',
    bullets: [
      'SBOM exists, cryptographic primitives inventoried.',
      'AES-256-GCM and SHA-256+ are the default symmetric primitives.',
      'Asymmetric crypto is still classical, but it is documented and assigned to a migration owner.',
    ],
  },
  {
    tier: 2, name: 'Quantum-ready', tone: 'bg-yellow-500/10 border-yellow-500/30 text-yellow-700 dark:text-yellow-500',
    bullets: [
      'TLS 1.3 enforced; TLS 1.2 disabled or scheduled for sunset.',
      'Hybrid KEM (X25519MLKEM768) negotiated where infrastructure supports it.',
      'OpenSSL 3.5+, BoringSSL, AWS s2n, BouncyCastle 1.78+ — libraries that can do hybrid are pinned.',
      'Documented migration plan referencing NSA CNSA 2.0 deadlines.',
      'The realistic 2026 target for production systems.',
    ],
  },
  {
    tier: 3, name: 'Quantum-resistant', tone: 'bg-blue-500/10 border-blue-500/30 text-blue-700 dark:text-blue-400',
    bullets: [
      'Pure post-quantum KEM in deployed surfaces; hybrid kept only for legacy clients.',
      'ML-DSA or SLH-DSA signatures where CAs and runtimes allow.',
      'PQ JWT signing where library maturity allows.',
    ],
  },
  {
    tier: 4, name: 'Fully migrated', tone: 'bg-india-green/15 border-india-green/40 text-india-green',
    bullets: [
      'Every cert chain — leaf, intermediate, root — uses PQ signatures.',
      'No classical fallback anywhere on the wire.',
      'PQ SSH host + user keys.',
      'Aspirational; needs CA + ecosystem support that is still arriving.',
    ],
  },
];

interface CheckRow {
  surface: string;
  what: string;
  signal: string;
}

const ENDPOINT_CHECKS: CheckRow[] = [
  { surface: 'TLS supported groups',  what: 'X25519MLKEM768 (codepoint 0x11ec) or the earlier X25519Kyber768Draft00 (0x6399).', signal: 'No hybrid KEM offered → medium finding ("blocks Tier 2"). Draft codepoint only → low finding ("upgrade to standard").' },
  { surface: 'TLS version',           what: 'Whether TLS 1.3 is supported. TLS 1.2 cannot negotiate hybrid KEM.',               signal: 'TLS 1.2 only → medium finding ("blocks Tier 2").' },
  { surface: 'Certificate signature', what: 'Leaf cert SubjectPublicKeyInfo + signature algorithm.',                              signal: 'RSA-2048/3072 + ECDSA P-256/P-384 → info ("future migration"). RSA-1024 → high (already broken classically).' },
  { surface: 'Cipher suite',          what: 'Negotiated symmetric cipher in the TLS session.',                                    signal: 'AES-128-GCM in 2026 → info ("AES-128 has ~64-bit security under Grover; prefer AES-256"). 3DES / RC4 / NULL → high.' },
  { surface: 'ALPN',                  what: 'HTTP/3 support. QUIC uses TLS 1.3, so the same hybrid story applies.',               signal: 'Reported in summary; no separate finding.' },
];

const REPO_CHECKS: CheckRow[] = [
  { surface: 'JWT signing algorithms',     what: 'RS256, ES256/ES384/ES512, PS256/PS384/PS512, EdDSA in jsonwebtoken / PyJWT / jose.', signal: 'All quantum-vulnerable. Flag with severity proportional to exposure.' },
  { surface: 'Asymmetric crypto API calls', what: 'cryptography.hazmat (Python), crypto.generateKeyPair (Node), KeyPairGenerator (Java), crypto/rsa + crypto/ecdsa (Go).', signal: 'Inventoried — drives migration plan.' },
  { surface: 'Hash + cipher patterns',     what: 'MD5, SHA-1, AES-128 default.',                                                   signal: 'MD5 / SHA-1 already broken classically — high. AES-128 → low (suboptimal for PQ).' },
  { surface: 'Library versions',           what: 'OpenSSL bundled with the runtime (Node, Python, Go); Bouncy Castle in JVM; PQ-capable crates in Rust.', signal: 'Old versions cannot do hybrid even if config flips — flag for upgrade.' },
  { surface: 'IaC TLS policy',             what: 'aws_lb_listener.ssl_policy, K8s ingress annotations, Caddy / Nginx ssl_ecdh_curve, Cloudflare Worker configs.', signal: 'Old security policies → medium. Recommend X25519MLKEM768 in ssl_ecdh_curve when available.' },
  { surface: 'SSH keys + deploy keys',     what: 'id_rsa, id_ecdsa, id_ed25519 in repo or referenced from CI.',                    signal: 'Hardcoded private key in repo → critical (independent of algorithm). Algorithm-level note added.' },
  { surface: 'Symmetric inventory',        what: 'Counts of AES-256-GCM vs AES-128-GCM vs ChaCha20-Poly1305.',                     signal: 'Drives the Tier 1/2 promotion logic.' },
];

export function QuantumReadinessGuidePage() {
  return (
    <div className="space-y-8 max-w-4xl">
      {/* Page header */}
      <div className="space-y-3">
        <Link to="/my/learn" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to Learn
        </Link>
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
            <Atom className="h-5 w-5 text-primary-strong" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Post-Quantum Readiness — what Trinetra evaluates today</h1>
            <p className="text-sm text-muted-foreground">
              The Quantum Readiness agent classifies a project on a 0–4 maturity scale against the post-quantum
              cryptography (PQC) transition — the move from classical RSA/ECC asymmetric crypto to NIST's PQC
              standards. This guide explains what gets checked, what each tier means, and which standards the
              recommendations are aligned to.
            </p>
          </div>
        </div>
      </div>

      {/* Section 1 — Why this matters */}
      <section className="space-y-3">
        <h2 className="text-lg font-bold text-foreground">1. Why post-quantum readiness matters now</h2>
        <p className="text-sm text-foreground/90">
          A cryptographically-relevant quantum computer (CRQC) doesn't exist yet, but two things make readiness an
          active engineering problem today:
        </p>
        <ul className="text-sm text-foreground/90 list-disc pl-6 space-y-2">
          <li>
            <strong>Harvest now, decrypt later.</strong> Adversaries can record encrypted traffic today and decrypt
            it the moment a CRQC arrives. Anything you transmit now with classical key exchange is at risk for the
            full lifetime of that data — for high-sensitivity workloads (regulated data, IP, communications),
            classical key exchange is already a bad bet.
          </li>
          <li>
            <strong>Migration takes years.</strong> Replacing crypto across cert chains, libraries, protocols, and
            keys is harder than swapping a hash function. NSA CNSA 2.0 expects national-security systems to be on
            PQ by 2030–2033; commercial migrations follow but cannot start late.
          </li>
        </ul>
      </section>

      {/* Section 2 — What's NOT broken by quantum */}
      <section className="space-y-3">
        <h2 className="text-lg font-bold text-foreground">2. Symmetric crypto is fine — the problem is asymmetric</h2>
        <p className="text-sm text-foreground/90">
          A common misconception is that "quantum breaks crypto." It does not. Quantum breaks{' '}
          <strong>asymmetric</strong> crypto via Shor's algorithm — RSA, ECDSA, ECDH, classical Diffie-Hellman are
          all rendered useless once a CRQC exists. Symmetric primitives are largely safe:
        </p>
        <Card className="p-4 bg-muted/30 text-sm">
          <ul className="space-y-1.5 list-disc pl-6 text-foreground/90">
            <li><strong>AES-256-GCM, ChaCha20-Poly1305</strong> — Grover halves the effective key strength but 128-bit security is still adequate.</li>
            <li><strong>SHA-256, SHA-3, HMAC, BLAKE2/3</strong> — same story; safe.</li>
            <li><strong>AES-128</strong> — drops to ~64-bit security under Grover. Recommended to upgrade to AES-256.</li>
            <li><strong>SHA-1, MD5</strong> — already classically broken. Flag regardless of PQ.</li>
          </ul>
        </Card>
        <p className="text-sm text-foreground/90">
          So the Quantum Readiness agent focuses on the asymmetric handshake and the symmetric strength sizing,
          not on cipher suites generally.
        </p>
      </section>

      {/* Section 3 — Maturity tiers */}
      <section className="space-y-3">
        <h2 className="text-lg font-bold text-foreground">3. The five maturity tiers</h2>
        <p className="text-sm text-foreground/90">
          Each project gets a single tier from 0 to 4. The tier is determined after all per-issue findings are
          emitted; the agent adds a summary finding that names which signals promoted or blocked the next tier.
        </p>
        <div className="space-y-3">
          {TIERS.map((t) => (
            <Card key={t.tier} className={cn('p-5 border', t.tone)}>
              <p className="text-xs font-bold uppercase tracking-wider mb-1">Tier {t.tier}</p>
              <p className="text-base font-semibold mb-2 text-foreground">{t.name}</p>
              <ul className="text-sm space-y-1 list-disc pl-5 text-foreground/90">
                {t.bullets.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
            </Card>
          ))}
        </div>
      </section>

      {/* Section 4 — What gets checked: endpoint */}
      <section className="space-y-3">
        <h2 className="text-lg font-bold text-foreground">4. What gets checked on endpoint projects</h2>
        <p className="text-sm text-foreground/90">
          For projects with an endpoint configured (URL + port), the agent runs the existing TLS probe and adds
          PQ-specific handshake tests:
        </p>
        <CheckTable rows={ENDPOINT_CHECKS} />
      </section>

      {/* Section 5 — What gets checked: repo */}
      <section className="space-y-3">
        <h2 className="text-lg font-bold text-foreground">5. What gets checked on repo projects</h2>
        <p className="text-sm text-foreground/90">
          For projects with a repo configured, the agent inspects source code, dependency manifests, and
          infrastructure-as-code definitions:
        </p>
        <CheckTable rows={REPO_CHECKS} />
      </section>

      {/* Section 6 — Where findings show up */}
      <section className="space-y-3">
        <h2 className="text-lg font-bold text-foreground">6. Where the findings flow</h2>
        <Card className="p-5">
          <ul className="text-sm space-y-2 list-disc pl-6 text-foreground/90">
            <li>
              <strong>Project findings list</strong> — every PQ-blocking issue appears as a regular finding, with
              severity proportional to exposure (critical / high / medium / low / info).
            </li>
            <li>
              <strong>Run report</strong> — the agent emits a single <em>info</em> summary finding{' '}
              <span className="font-mono text-xs">"Quantum Readiness Tier — N"</span> with the next 3–5 concrete
              steps to reach the next tier.
            </li>
            <li>
              <strong>Compliance Posture</strong> — quantum-readiness findings map to{' '}
              <span className="font-mono text-xs">CRY-01</span> (Use of Cryptography),{' '}
              <span className="font-mono text-xs">CRY-05</span> (In-Transit),{' '}
              <span className="font-mono text-xs">CRY-09</span> (Key Management),{' '}
              <span className="font-mono text-xs">NET-08</span> (Transmission Cryptographic Controls), and{' '}
              <span className="font-mono text-xs">TPM-08</span> (Supply-Chain) in the Secure Controls Framework —
              so they roll up automatically into your selected compliance frameworks.
            </li>
          </ul>
        </Card>
      </section>

      {/* Section 7 — Standards alignment */}
      <section className="space-y-3">
        <h2 className="text-lg font-bold text-foreground">7. Standards the recommendations align to</h2>
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="h-4 w-4 text-india-green" />
            <p className="text-sm font-semibold text-foreground">Reference baseline (April 2026)</p>
          </div>
          <ul className="text-sm space-y-2 list-disc pl-6 text-foreground/90">
            <li>
              <strong>NIST FIPS 203</strong> — ML-KEM (formerly Kyber); the standardised post-quantum key
              encapsulation mechanism.
            </li>
            <li>
              <strong>NIST FIPS 204 / 205 / 206</strong> — ML-DSA (Dilithium), SLH-DSA (SPHINCS+), FN-DSA (FALCON);
              the standardised post-quantum digital signature algorithms.
            </li>
            <li>
              <strong>NSA CNSA 2.0</strong> — National-security systems must migrate by 2030–2033. Sets the de facto
              ceiling on commercial timelines.
            </li>
            <li>
              <strong>BSI TR-02102, ANSSI, NCSC</strong> — All recommend hybrid PQ today rather than waiting.
            </li>
            <li>
              <strong>OMB M-23-02</strong> — US federal agencies must inventory cryptographic systems and feed the
              CISA migration roadmap.
            </li>
            <li>
              <strong>TLS hybrid KEM</strong> — <span className="font-mono text-xs">X25519MLKEM768</span>{' '}
              (codepoint <span className="font-mono text-xs">0x11ec</span>) is the production target. The earlier
              draft <span className="font-mono text-xs">X25519Kyber768Draft00</span>{' '}
              (<span className="font-mono text-xs">0x6399</span>) is what Cloudflare, Google, and Apple deployed
              through 2024–2025; many servers still negotiate it.
            </li>
          </ul>
        </Card>
      </section>

      {/* Section 8 — What's not in scope */}
      <section className="space-y-3">
        <h2 className="text-lg font-bold text-foreground">8. What's intentionally not in scope (yet)</h2>
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-4 w-4 text-saffron" />
            <p className="text-sm font-semibold text-foreground">Limits of static + handshake analysis</p>
          </div>
          <ul className="text-sm space-y-2 list-disc pl-6 text-foreground/90">
            <li>
              <strong>HSM / KMS internals.</strong> AWS KMS, Azure Key Vault, GCP KMS — we can detect <em>that</em>{' '}
              a project uses them but not <em>which</em> algorithms they use internally. Provider PQ roadmaps cover this.
            </li>
            <li>
              <strong>Hardware accelerators / TPMs.</strong> Out of reach of static + network probing.
            </li>
            <li>
              <strong>PQ certificate chain issuance.</strong> Public CAs are still piloting hybrid intermediates;
              PQ leaf certs aren't broadly available. The agent flags classical cert chains as <em>info</em> rather
              than <em>high</em> until that ecosystem catches up.
            </li>
            <li>
              <strong>End-of-life crypto in stripped binaries.</strong> Without an SBOM you can't see what static-
              linked crypto is shipped — link the agent's output with a real SBOM tool when you need that view.
            </li>
          </ul>
        </Card>
      </section>

      {/* Section 9 — How to run it */}
      <section className="space-y-3">
        <h2 className="text-lg font-bold text-foreground">9. Running the agent</h2>
        <Card className="p-5">
          <ul className="text-sm space-y-2 list-disc pl-6 text-foreground/90">
            <li>The agent appears in the Agents picker on every project page (repo, endpoint, or hybrid).</li>
            <li>
              No additional configuration is required — it inherits the project's existing endpoint or repo config.
            </li>
            <li>
              Average run time: 30–90 seconds for a small repo or single endpoint; longer for monorepos with many
              dependency manifests.
            </li>
            <li>
              For continuous coverage, schedule it from the project's Agents page — quarterly is a reasonable
              baseline for a stable codebase, more often around a major dependency or TLS-policy change.
            </li>
          </ul>
        </Card>
      </section>

      <p className="text-[11px] text-muted-foreground italic">
        References:{' '}
        <a className="underline" href="https://csrc.nist.gov/projects/post-quantum-cryptography" target="_blank" rel="noreferrer">NIST PQC <ExternalLink className="h-3 w-3 inline" /></a>{' '}·{' '}
        <a className="underline" href="https://media.defense.gov/2022/Sep/07/2003071834/-1/-1/0/CSA_CNSA_2.0_ALGORITHMS_.PDF" target="_blank" rel="noreferrer">NSA CNSA 2.0 <ExternalLink className="h-3 w-3 inline" /></a>{' '}·{' '}
        <a className="underline" href="https://www.bsi.bund.de/EN/Themen/Unternehmen-und-Organisationen/Standards-und-Zertifizierung/Technische-Richtlinien/TR-nach-Thema-sortiert/tr02102/tr02102_node.html" target="_blank" rel="noreferrer">BSI TR-02102 <ExternalLink className="h-3 w-3 inline" /></a>
      </p>
    </div>
  );
}

function CheckTable({ rows }: { rows: CheckRow[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left py-2 px-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider">Surface</th>
            <th className="text-left py-2 px-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider">What gets inspected</th>
            <th className="text-left py-2 px-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider">Signal</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-border/60 align-top">
              <td className="py-2 px-3 font-semibold text-foreground">{row.surface}</td>
              <td className="py-2 px-3 text-foreground/90">{row.what}</td>
              <td className="py-2 px-3 text-muted-foreground">{row.signal}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
